import { z } from 'zod';

import { EmailStringSchema } from '../../common';
import {
  ExperienceImageSizes,
  ExperienceImageSizeWithOriginalInUriSchema,
  ExperienceKind,
  ExperienceStatus,
  ExperienceVersion,
  MediaLinkResolution,
  MediaType,
} from '../../experience/const';
import { HostSchema } from '../common';
import { CustomQuestionSchema } from '../experience/custom-questions';
import { ExperienceSettingsSchema } from '../experience/experience-settings';
import {
  ExperienceType,
  BookingAvailabilityModeValue,
  ExperienceNotificationOverridesSchema,
  IntegrationMetaSchema,
  TaxSchema,
} from '../experience/experiences';
import { PositionSchema } from '../experience/position';
import { PriceTierSchema, PriceTierRulesSchema } from '../experience/price-tier-rules';
import { ShapeSchema } from '../experience/shape';
import { ListingPictureKind, IMediaReferenceSchema } from '../media/media';
import { PaymentMethods } from '../payment-method/public';

export const ExperienceImageSizeMediaSchema = z.object({
  id: z.string().uuid(),
  uri: ExperienceImageSizeWithOriginalInUriSchema,
});

export const MediaLinkSchema = z.object({
  type: z.nativeEnum(MediaType),
  url: z.string().url(),
  resolution: z.nativeEnum({ ...MediaLinkResolution, ...ExperienceImageSizes }),
});

export const MediaInputSchema = z.object({
  id: z.string().uuid(),
  kind: z.nativeEnum(ListingPictureKind),
});

export const MediaSchema = MediaInputSchema.partial({ kind: true }).extend({
  order: z.number().int().min(0).optional(),
  type: z.nativeEnum(MediaType),
  links: z.array(MediaLinkSchema),
  altText: z.string().nullable().optional(),
});

export enum ResourceGroupCollectionEventType {
  GENERAL_ADMISSION = 'general_admission',
  RESERVED_SEATING = 'reserved_seating',
  AUCTION = 'auction',
}

export const ExperienceSchema = z.object({
  id: z.string().uuid(),
  title: z.string().nonempty(),
  description: z.string().nonempty().nullable(),
  status: z.nativeEnum(ExperienceStatus),
  version: z.nativeEnum(ExperienceVersion),
  coverPicture: IMediaReferenceSchema.nullable(),
  descriptionPicture: IMediaReferenceSchema.nullable(),
  isBookable: z.boolean(),
  isNoHost: z.boolean(),
  sessionDurationMinutes: z.number().min(1).nullable(),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).nullable(),
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).nullable(),
  shouldValidateComplimentaryBooking: z.object({ memberCharge: z.boolean(), roomCharge: z.boolean() }).partial().nullable(),
  onlineBookingCutoffHours: z.number().nullable(),
  firstAvailableDate: z
    .object({
      scheduledDate: z.string(),
      sessions: z.array(
        z.object({
          startTime: z.string(),
          supportedParticipantsCount: z.number(),
          priceTiers: z.array(PriceTierSchema),
          withinOnlineBookingCutoffWindow: z.boolean().optional(),
          availableQuantityPerTier: z.record(z.number()).optional(),
        }),
      ),
    })
    .nullable(),
  isUnlisted: z.boolean().nullable(),
  visibleOnCalendar: z.boolean().optional(),
  hostedBy: HostSchema,
  category: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  hasPublishedVersion: z.boolean(),
  isArchived: z.boolean(),
  createdByAi: z.boolean(),
  settings: ExperienceSettingsSchema.nullable(),
  kind: z.nativeEnum(ExperienceKind).nullable(),
  minimumRequiredToBook: z.number().nullable(),
  maximumAllowedToBook: z.number().nullable(),
  number: z.string().nullable(),
  displayedCapacity: z.number().int().nullable(),
  shape: ShapeSchema.nullable(),
  position: PositionSchema.nullable(),
  maxParticipantsCount: z.number().int().nonnegative().nullable(),
  timezone: z.string(),
  emailMessage: z.string().nullable(),
  notificationEmails: z.array(EmailStringSchema).max(10).nullable(),
  notificationOverrides: ExperienceNotificationOverridesSchema.nullable(),
  integrationMeta: z.array(IntegrationMetaSchema).nullable(),
  taxes: z.array(TaxSchema).nullable(),
  type: z.nativeEnum(ExperienceType),
  isTicketed: z.boolean().optional(),
  cancellationPolicyId: z.string().uuid().nullable(),
  reschedulePolicyId: z.string().uuid().nullable(),
  limitedTimeId: z.string().uuid().nullable(),
  customCallToAction: z.string().nullable(),
});

export const CustomQuestionInResourcesSchema = CustomQuestionSchema.extend({
  id: z.union([CustomQuestionSchema.shape.id, z.literal('emailAddress'), z.literal('phoneNumber')]),
});

export type CustomQuestionInResources = z.output<typeof CustomQuestionInResourcesSchema>;

export const ExperienceAsResourceSchema = ExperienceSchema.pick({
  id: true,
  title: true,
  description: true,
  status: true,
  version: true,
  isUnlisted: true,
  visibleOnCalendar: true,
  displayedCapacity: true,
  position: true,
  onlineBookingCutoffHours: true,
  emailMessage: true,
  notificationOverrides: true,
  notificationEmails: true,
  paymentMethods: true,
  kind: true,
  shouldValidateComplimentaryBooking: true,
  integrationMeta: true,
  sessionDurationMinutes: true,
  taxes: true,
  type: true,
  maximumAllowedToBook: true,
  minimumRequiredToBook: true,
  maxParticipantsCount: true,
  bookingAvailabilityMode: true,
  isTicketed: true,
  cancellationPolicyId: true,
  reschedulePolicyId: true,
  limitedTimeId: true,
}).extend({
  /** Pool-backed groups only: the pool unit this resource sells. */
  resourcePoolUnitId: z.string().uuid().nullable().optional(),
  shape: ExperienceSchema.shape.shape,
  customQuestions: z.array(CustomQuestionInResourcesSchema),
  priceTiers: z.array(PriceTierSchema).optional(),
  priceTierRules: z.array(PriceTierRulesSchema).optional(),
  number: ExperienceSchema.shape.number.unwrap(),
});

export enum LocationField {
  NAME = 'name',
  ADDITIONAL_INFO = 'additionalInfo',
}
